import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Save, KeyRound, ShieldCheck, Info, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  // Google API key setter
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [clearingKey, setClearingKey] = useState(false);

  const reloadSettings = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setSettings(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const saveGoogleKey = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setSavingKey(true);
    try {
      await api.post("/settings/google-api-key", { api_key: apiKey.trim() });
      toast.success("Google Maps API-Key gespeichert.");
      setApiKey("");
      setShowKey(false);
      await reloadSettings();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingKey(false);
    }
  };

  const clearGoogleKey = async () => {
    setClearingKey(true);
    try {
      await api.delete("/settings/google-api-key");
      toast.success("Google Maps API-Key entfernt.");
      await reloadSettings();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClearingKey(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.patch("/settings", {
        company_name: settings.company_name,
        product_name: settings.product_name,
        public_base_url: settings.public_base_url,
      });
      setSettings(data);
      toast.success("Einstellungen gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error("Passwörter stimmen nicht überein.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Passwort geändert. Bitte erneut anmelden.");
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setTimeout(() => logout(), 800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  if (!settings) {
    return (
      <div className="text-muted-foreground text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <header>
        <p className="label-eyebrow">Konfiguration</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Einstellungen</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Firmen-Daten, öffentliche URL und Google-Integration.
        </p>
      </header>

      <form onSubmit={save} className="card-surface p-5 space-y-5" data-testid="settings-form">
        <div className="grid gap-4">
          <FormField label="Firmenname (Agentur)">
            <Input
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              className="bg-background/50 h-11"
              data-testid="settings-company-name"
            />
          </FormField>
          <FormField label="Produktname">
            <Input
              value={settings.product_name}
              onChange={(e) => setSettings({ ...settings, product_name: e.target.value })}
              className="bg-background/50 h-11"
              data-testid="settings-product-name"
            />
          </FormField>
          <FormField
            label="Öffentliche Basis-URL"
            hint='Wird für NFC/QR-Codes verwendet. Beispiel: "https://review.1ai-agency.com" — QR-Codes zeigen dann /r/{slug}.'
          >
            <Input
              value={settings.public_base_url}
              onChange={(e) =>
                setSettings({ ...settings, public_base_url: e.target.value.trim() })
              }
              className="bg-background/50 h-11 font-mono text-xs"
              data-testid="settings-base-url"
            />
          </FormField>
        </div>

        <div>
          <Button
            type="submit"
            className="rounded-full h-11 px-5 gap-2"
            disabled={savingSettings}
            data-testid="settings-save-btn"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </Button>
        </div>
      </form>

      {/* Google Maps API state + key setter */}
      <div className="card-surface p-5" data-testid="settings-google-status-card">
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-lg grid place-items-center ${
              settings.google_maps_api_configured
                ? "bg-emerald-500/10 text-emerald-400"
                : "bg-amber-500/10 text-amber-400"
            }`}
          >
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-semibold">Google Maps API</h3>
              <span
                data-testid="google-status-indicator"
                className={`text-[11px] uppercase tracking-widest font-semibold ${
                  settings.google_maps_api_configured
                    ? "text-emerald-400"
                    : "text-amber-400"
                }`}
              >
                {settings.google_maps_api_configured ? "Aktiv" : "Nicht eingerichtet"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {settings.google_maps_api_configured
                ? "Places API (New) ist aktiv. Suche und writeAReviewUri werden über deinen Server-seitigen API-Key abgefragt."
                : "Google Maps API-Key noch nicht eingerichtet."}
            </p>
          </div>
        </div>

        <Separator className="bg-white/[0.06] my-5" />

        <form onSubmit={saveGoogleKey} className="space-y-3" data-testid="google-key-form">
          <FormField
            label={settings.google_maps_api_configured ? "API-Key ersetzen" : "API-Key eintragen"}
            hint="Dein Key wird ausschliesslich serverseitig verwendet und nie im Browser angezeigt oder geloggt."
          >
            <div className="relative">
              <Input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="AIza…"
                autoComplete="off"
                spellCheck={false}
                className="bg-background/50 h-11 pr-10 font-mono text-xs"
                data-testid="google-key-input"
                minLength={10}
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                data-testid="google-key-toggle-visibility"
                aria-label={showKey ? "API-Key verbergen" : "API-Key anzeigen"}
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </FormField>
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              disabled={savingKey || !apiKey.trim()}
              className="rounded-full h-11 px-5 gap-2"
              data-testid="google-key-save-btn"
            >
              {savingKey ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {settings.google_maps_api_configured ? "Key ersetzen" : "Key speichern"}
            </Button>
            {settings.google_maps_api_configured ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="rounded-full h-11 px-4 gap-2 text-muted-foreground hover:text-destructive"
                    data-testid="google-key-clear-btn"
                    disabled={clearingKey}
                  >
                    <Trash2 className="w-4 h-4" /> Key entfernen
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>API-Key wirklich entfernen?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Ohne Key funktioniert die Google-Firmen-Suche nicht mehr. Bereits
                      angelegte Kunden bleiben unberührt.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={clearGoogleKey}
                      className="bg-destructive hover:bg-destructive/90"
                      data-testid="google-key-clear-confirm"
                    >
                      Endgültig entfernen
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : null}
          </div>
        </form>

        <div className="mt-4 text-xs text-muted-foreground bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 space-y-1.5">
          <div className="flex items-center gap-1.5 text-foreground">
            <Info className="w-3.5 h-3.5" /> Empfohlene Restrictions in der Google Cloud Console:
          </div>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>
              <strong>Application restrictions:</strong> IP-Adresse deines Servers
              (nicht „HTTP referrers", nicht „Android/iOS").
            </li>
            <li>
              <strong>API restrictions:</strong> nur <strong>Places API (New)</strong> aktivieren.
            </li>
            <li>
              Optional: Tageskontingent + Billing-Alerts als Anti-Runaway-Schutz.
            </li>
          </ul>
        </div>
      </div>

      {/* Change password */}
      <form
        onSubmit={changePassword}
        className="card-surface p-5 space-y-5"
        data-testid="change-password-form"
      >
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Passwort ändern</h3>
        </div>
        <Separator className="bg-white/[0.06]" />

        <div className="grid gap-4">
          <FormField label="Aktuelles Passwort">
            <Input
              type="password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              className="bg-background/50 h-11"
              autoComplete="current-password"
              required
              data-testid="settings-current-password"
            />
          </FormField>
          <FormField label="Neues Passwort" hint="Mindestens 8 Zeichen.">
            <Input
              type="password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              minLength={8}
              className="bg-background/50 h-11"
              autoComplete="new-password"
              required
              data-testid="settings-new-password"
            />
          </FormField>
          <FormField label="Neues Passwort bestätigen">
            <Input
              type="password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              minLength={8}
              className="bg-background/50 h-11"
              autoComplete="new-password"
              required
              data-testid="settings-confirm-password"
            />
          </FormField>
        </div>
        <div className="text-xs text-muted-foreground">
          Aktuell angemeldet als <strong>{user?.email}</strong>.
        </div>
        <div>
          <Button
            type="submit"
            className="rounded-full h-11 px-5 gap-2"
            disabled={savingPw || !pwCurrent || !pwNew || pwNew.length < 8}
            data-testid="change-password-btn"
          >
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Passwort aktualisieren
          </Button>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint ? <p className="text-xs text-muted-foreground mt-1.5">{hint}</p> : null}
    </div>
  );
}
